import type {
  ComponentPropsWithoutRef,
  ElementType,
  HTMLAttributes,
  PropsWithChildren,
  ReactNode,
} from "react";
import { cn } from "../../utils";

export type SurfaceVariant =
  | "default"
  | "settings"
  | "dashboard"
  | "accent"
  | "ghost";
export type SurfacePadding = "comfortable" | "compact" | "bleed";
export type SurfaceElevation = "none" | "surface" | "floating";

export interface SurfaceProps extends HTMLAttributes<HTMLElement> {
  as?: ElementType;
  variant?: SurfaceVariant;
  padding?: SurfacePadding;
  elevation?: SurfaceElevation;
}

interface SurfaceSectionProps extends HTMLAttributes<HTMLDivElement> {
  padded?: boolean;
}

const surfaceVariantClassName: Record<SurfaceVariant, string> = {
  default: "border-border-subtle bg-surface-primary text-content-primary",
  settings: "border-border-subtle bg-surface-primary text-content-primary",
  dashboard: "border-border-subtle bg-surface-primary text-content-primary",
  accent: "border-transparent bg-surface-accent text-content-inverse",
  ghost: "border-border-subtle bg-surface-primary/80 text-content-primary",
};

const elevationClassName: Record<SurfaceElevation, string> = {
  none: "shadow-none",
  surface: "shadow-surface",
  floating: "shadow-floating",
};

const paddingClassName: Record<SurfacePadding, string> = {
  comfortable: "px-surface-comfortable-x py-surface-comfortable-y",
  compact: "px-surface-compact-x py-surface-compact-y",
  bleed: "p-0",
};

export function Surface({
  as: Component = "section",
  variant = "default",
  padding = "comfortable",
  elevation = "surface",
  className,
  children,
  ...props
}: PropsWithChildren<SurfaceProps>) {
  return (
    <Component
      className={cn(
        "relative isolate overflow-hidden rounded-surface border transition-colors duration-fast ease-standard",
        paddingClassName[padding],
        surfaceVariantClassName[variant],
        elevationClassName[elevation],
        className,
      )}
      {...props}
    >
      {children}
    </Component>
  );
}

export function SurfaceHeader({
  className,
  padded = false,
  ...props
}: SurfaceSectionProps) {
  return (
    <div
      className={cn(
        "flex items-start justify-between gap-4",
        padded && "px-surface-comfortable-x pt-surface-comfortable-y",
        className,
      )}
      {...props}
    />
  );
}

export function SurfaceBody({
  className,
  padded = false,
  ...props
}: SurfaceSectionProps) {
  return (
    <div
      className={cn(
        padded && "px-surface-comfortable-x py-surface-comfortable-y",
        className,
      )}
      {...props}
    />
  );
}

export function SurfaceFooter({
  className,
  padded = false,
  ...props
}: SurfaceSectionProps) {
  return (
    <div
      className={cn(
        "border-t border-border-subtle bg-surface-secondary/80",
        padded && "px-surface-comfortable-x py-surface-compact-y",
        className,
      )}
      {...props}
    />
  );
}

interface SurfaceTitleProps {
  title: string;
  description?: string;
  meta?: ReactNode;
  titleClassName?: string;
  descriptionClassName?: string;
}

export function SurfaceTitle({
  title,
  description,
  meta,
  titleClassName,
  descriptionClassName,
}: SurfaceTitleProps) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <h3
          className={cn(
            "text-heading-sm font-semibold text-content-primary",
            titleClassName,
          )}
        >
          {title}
        </h3>
        {meta}
      </div>
      {description ? (
        <p className={cn("text-body text-content-muted", descriptionClassName)}>
          {description}
        </p>
      ) : null}
    </div>
  );
}

export type SurfaceComponentProps<T extends ElementType> =
  ComponentPropsWithoutRef<T> & SurfaceProps;
